import { groupBy } from 'lodash';
import { isEmpty } from 'lodash/fp';

import { CauseOutcome, CroRecords } from '../models/data-type';
import { CauseEntity, DataType, RaceEntity, RawRaceEntity } from '../types';
import { mapConditionToValue } from './chart-data-helpers';

export type HierarchyByLevels<T> = Record<number, T[]>;

/** Item in a cause/sequela/CRO-style hierarchy with encoded string id and level N parent refs */
export type CauseHierarchyItem = {
    id: string;
    level: number;
    [key: string]: unknown;
};

/**
 * Maps raw cause hierarchy metadata (numeric ids) to CauseHierarchyItem[] with encoded string ids.
 * Pure helper for use in ConditionDropdown and elsewhere.
 */
export function buildTypedCauseHierarchy(
    causeMetadataWithHierarchy: CauseEntity[]
): CauseHierarchyItem[] {
    return causeMetadataWithHierarchy.map((cause) => {
        const encode = (
            idField: keyof Pick<
                CauseEntity,
                | 'id'
                | 'level0_parent_id'
                | 'level1_parent_id'
                | 'level2_parent_id'
                | 'level3_parent_id'
            >
        ) =>
            mapConditionToValue({
                primary_entity_id: cause[idField],
                data_type: CauseOutcome,
            });
        return {
            id: encode('id'),
            level: cause.level,
            level0_parent_id: encode('level0_parent_id'),
            level1_parent_id: encode('level1_parent_id'),
            level2_parent_id: encode('level2_parent_id'),
            level3_parent_id: encode('level3_parent_id'),
        };
    });
}

export type HierarchyByLevelsResult<T> = {
    rootLevelItems: T[];
    rootLevel: number | undefined;
    hierarchyByLevels: HierarchyByLevels<T>;
};

/** Groups hierarchy by level and returns root (top) level and items. Safe for empty input. */
export function getHierarchyByLevels<T extends { level: number }>(
    hierarchy: T[]
): HierarchyByLevelsResult<T> {
    const hierarchyByLevels = groupBy(hierarchy, 'level') as HierarchyByLevels<T>;
    const levelKeys = Object.keys(hierarchyByLevels)
        .map((level) => parseInt(level, 10))
        .filter((n) => !Number.isNaN(n));

    if (isEmpty(levelKeys)) {
        return {
            rootLevelItems: [],
            rootLevel: undefined,
            hierarchyByLevels: {} as HierarchyByLevels<T>,
        };
    }

    const rootLevel = Math.min(...levelKeys);
    const rootLevelItems = hierarchyByLevels[rootLevel];

    if (!Array.isArray(rootLevelItems)) {
        return {
            rootLevelItems: [],
            rootLevel: undefined,
            hierarchyByLevels,
        };
    }

    return { rootLevelItems, rootLevel, hierarchyByLevels };
}

/**
 * Runs the callback with grouped hierarchy and root level only when hierarchy has a root level.
 * Otherwise returns emptyValue. Use when building tree/options from a flat hierarchy.
 */
export function whenHierarchyHasRoot<T extends { level: number }, R>(
    hierarchy: T[],
    map: (result: {
        rootLevelItems: T[];
        rootLevel: number;
        hierarchyByLevels: HierarchyByLevels<T>;
    }) => R,
    emptyValue: R
): R {
    const { rootLevel, rootLevelItems, hierarchyByLevels } = getHierarchyByLevels(hierarchy);
    if (rootLevel === undefined || isEmpty(rootLevelItems)) {
        return emptyValue;
    }
    return map({
        rootLevelItems,
        rootLevel,
        hierarchyByLevels,
    });
}

/** Transforms flat race API response into hierarchical RaceEntity[] with level and parent chain info */
export const buildRaceHierarchy = (races: RawRaceEntity[]): RaceEntity[] => {
    const byParentId = groupBy(races, 'parent_id');
    const visited = new Set<number>();
    const result: RaceEntity[] = [];

    const visit = (item: RawRaceEntity, parentChain: number[], level: number) => {
        if (visited.has(item.id)) return;
        visited.add(item.id);

        const entity: RaceEntity = {
            id: item.id,
            name: item.name,
            parent_id: item.parent_id,
            level,
            level0_parent_id: parentChain[0],
            level1_parent_id: parentChain[1],
            level2_parent_id: parentChain[2],
        };

        result.push(entity);

        (byParentId[item.id] || []).forEach((child: RawRaceEntity) =>
            visit(child, [...parentChain, item.id], level + 1)
        );
    };

    const roots = byParentId[0] || [];
    roots.forEach((root: RawRaceEntity) => visit(root, [], 0));

    races.filter((r) => !visited.has(r.id)).forEach((r) => visit(r, [], 0));

    return result;
};

export type SequelaLikeChild = {
    id: number;
    parent_cause_id: number;
    level: number;
    level0_parent_id: number;
};

export function attachChildEntitiesUnderCauses(
    causeHierarchy: CauseHierarchyItem[],
    childItems: SequelaLikeChild[],
    dataType: DataType
): CauseHierarchyItem[] {
    const getEncodedCauseId = (causeId: number) =>
        mapConditionToValue({ data_type: CauseOutcome, primary_entity_id: causeId });
    const encodeChildId = (child: SequelaLikeChild) =>
        mapConditionToValue({ data_type: dataType, primary_entity_id: child.id });
    const getChildLevel = (child: SequelaLikeChild, parentLevel: number) =>
        parentLevel + child.level + 1;
    // For sequela, level0_parent_id is another sequela; for CRO it's the cause (normalized in epic).
    const level0ParentDataType = dataType === CroRecords ? CauseOutcome : dataType;
    const extendParentIds = (
        child: SequelaLikeChild,
        parentCause: CauseHierarchyItem,
        baseParentIds: Record<string, string>
    ) => ({
        ...baseParentIds,
        [`level${parentCause.level + 1}_parent_id`]: mapConditionToValue({
            data_type: level0ParentDataType,
            primary_entity_id: child.level0_parent_id,
        }),
    });

    const result: CauseHierarchyItem[] = [];
    const causeById = new Map<string, CauseHierarchyItem>(
        causeHierarchy.map((cause) => [cause.id, cause])
    );

    for (const child of childItems) {
        const parentCauseId = child.parent_cause_id;
        if (parentCauseId == null) continue;

        const encodedParentId = getEncodedCauseId(parentCauseId);
        const parentCause = causeById.get(encodedParentId);

        if (!parentCause) continue;

        const parentIds: Record<string, string> = {};
        for (let counter = 0; counter < parentCause.level; ++counter) {
            parentIds[`level${counter}_parent_id`] = parentCause[
                `level${counter}_parent_id`
            ] as string;
        }
        parentIds[`level${parentCause.level}_parent_id`] = parentCause.id;

        const finalParentIds = extendParentIds(child, parentCause, parentIds);

        result.push({
            id: encodeChildId(child),
            level: getChildLevel(child, parentCause.level),
            ...finalParentIds,
        });
    }

    return result;
}

export function findMissingIds<T extends number | string>(
    allowedIds: T[],
    includedIds: Set<T>
): T[] {
    return allowedIds.filter((id) => !includedIds.has(id));
}
